// Inhoud van het inlogkaartje per view ('login', 'forgotPassword', 'resetSent').
// De drie views zijn render-functies (geen eigen componenten), zodat React de
// elementen bij een viewwissel net zo hergebruikt als vóór de opsplitsing.
import React from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  CircularProgress,
  Link as MuiLink,
  IconButton,
  InputAdornment,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Email,
  Lock,
  Login as LoginIcon,
  Shield,
  Security,
} from '@mui/icons-material';
import { motion } from 'framer-motion';

const buttonVariants = {
  hover: {
    scale: 1.02,
    transition: {
      duration: 0.2,
      ease: 'easeInOut',
    },
  },
  tap: {
    scale: 0.98,
  },
};

function renderForgotPassword({ theme, email, setEmail, loading, setView, onForgotPassword }) {
  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.3 }}
    >
      <Box component="form" onSubmit={onForgotPassword} sx={{ mt: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <Security sx={{ color: 'primary.main', mr: 1, fontSize: 28 }} />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Wachtwoord herstellen
          </Typography>
        </Box>
        <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary', lineHeight: 1.6 }}>
          Geen zorgen! Voer je e-mailadres in en we sturen je een beveiligde link om je wachtwoord
          opnieuw in te stellen.
        </Typography>
        <TextField
          margin="normal"
          required
          fullWidth
          label="E-mail"
          variant="outlined"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          // eslint-disable-next-line jsx-a11y/no-autofocus -- bewust: e-mailveld is het startpunt van het inlogformulier
          autoFocus
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
              transition: 'all 0.3s ease',
              '&:hover': {
                boxShadow: `0 0 0 2px ${theme.palette.primary.main}20`,
              },
              '&.Mui-focused': {
                boxShadow: `0 0 0 2px ${theme.palette.primary.main}40`,
              },
            },
          }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <Email sx={{ color: 'primary.main' }} />
                </InputAdornment>
              ),
            },
          }}
        />
        <motion.div variants={buttonVariants} whileHover="hover" whileTap="tap">
          <Button
            variant="contained"
            type="submit"
            fullWidth
            disabled={loading}
            sx={{
              mt: 3,
              mb: 2,
              py: 1.5,
              borderRadius: 3,
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
              boxShadow: `0 4px 12px ${theme.palette.primary.main}40`,
              fontSize: '1.1rem',
              fontWeight: 600,
              '&:hover': {
                background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
                boxShadow: `0 6px 20px ${theme.palette.primary.main}60`,
              },
            }}
          >
            {loading ? <CircularProgress size={24} color="inherit" /> : 'Herstellink versturen'}
          </Button>
        </motion.div>
        <MuiLink
          component="button"
          variant="body2"
          onClick={() => setView('login')}
          sx={{
            display: 'block',
            textAlign: 'center',
            color: 'primary.main',
            fontWeight: 500,
            textDecoration: 'none',
            '&:hover': {
              textDecoration: 'underline',
            },
          }}
        >
          ← Terug naar inloggen
        </MuiLink>
      </Box>
    </motion.div>
  );
}

function renderResetSent({ theme, email, setView }) {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.4 }}
    >
      <Box sx={{ textAlign: 'center' }}>
        <Box sx={{ mb: 3 }}>
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
          >
            <Shield sx={{ fontSize: 60, color: 'success.main', mb: 2 }} />
          </motion.div>
        </Box>
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: 'text.primary' }}>
          Check je e-mail
        </Typography>
        <Typography variant="body1" sx={{ mb: 4, color: 'text.secondary', lineHeight: 1.6 }}>
          We hebben een beveiligde herstellink gestuurd naar
          <br />
          <strong style={{ color: theme.palette.primary.main }}>{email}</strong>
        </Typography>
        <motion.div variants={buttonVariants} whileHover="hover" whileTap="tap">
          <Button
            variant="contained"
            onClick={() => setView('login')}
            fullWidth
            sx={{
              py: 1.5,
              borderRadius: 3,
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
              fontSize: '1.1rem',
              fontWeight: 600,
            }}
          >
            Terug naar inloggen
          </Button>
        </motion.div>
      </Box>
    </motion.div>
  );
}

function renderLoginForm({
  theme,
  email,
  setEmail,
  password,
  setPassword,
  showPassword,
  setShowPassword,
  loading,
  setView,
  onLogin,
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
    >
      <Box component="form" onSubmit={onLogin} sx={{ mt: 1 }}>
        <TextField
          margin="normal"
          required
          fullWidth
          label="E-mail"
          variant="outlined"
          type="email"
          autoComplete="username"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          // eslint-disable-next-line jsx-a11y/no-autofocus -- bewust: e-mailveld is het startpunt van het inlogformulier
          autoFocus
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
              transition: 'all 0.3s ease',
              '&:hover': {
                boxShadow: `0 0 0 2px ${theme.palette.primary.main}20`,
              },
              '&.Mui-focused': {
                boxShadow: `0 0 0 2px ${theme.palette.primary.main}40`,
              },
            },
          }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <Email sx={{ color: 'primary.main' }} />
                </InputAdornment>
              ),
            },
          }}
        />
        <TextField
          margin="normal"
          required
          fullWidth
          label="Wachtwoord"
          variant="outlined"
          type={showPassword ? 'text' : 'password'}
          autoComplete="current-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
              transition: 'all 0.3s ease',
              '&:hover': {
                boxShadow: `0 0 0 2px ${theme.palette.primary.main}20`,
              },
              '&.Mui-focused': {
                boxShadow: `0 0 0 2px ${theme.palette.primary.main}40`,
              },
            },
          }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <Lock sx={{ color: 'primary.main' }} />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label={showPassword ? 'Wachtwoord verbergen' : 'Wachtwoord tonen'}
                    edge="end"
                    sx={{ color: 'text.secondary' }}
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            },
          }}
        />
        <motion.div variants={buttonVariants} whileHover="hover" whileTap="tap">
          <Button
            variant="contained"
            type="submit"
            fullWidth
            disabled={loading}
            startIcon={loading ? null : <LoginIcon />}
            sx={{
              mt: 3,
              mb: 2,
              py: 1.5,
              borderRadius: 3,
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
              boxShadow: `0 4px 12px ${theme.palette.primary.main}40`,
              fontSize: '1.1rem',
              fontWeight: 600,
              '&:hover': {
                background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
                boxShadow: `0 6px 20px ${theme.palette.primary.main}60`,
              },
            }}
          >
            {loading ? <CircularProgress size={24} color="inherit" /> : 'Veilig Inloggen'}
          </Button>
        </motion.div>
        <MuiLink
          component="button"
          variant="body2"
          onClick={() => setView('forgotPassword')}
          sx={{
            display: 'block',
            textAlign: 'center',
            color: 'primary.main',
            fontWeight: 500,
            textDecoration: 'none',
            '&:hover': {
              textDecoration: 'underline',
            },
          }}
        >
          Wachtwoord vergeten?
        </MuiLink>
      </Box>
    </motion.div>
  );
}

export default function LoginViewContent(props) {
  switch (props.view) {
    case 'forgotPassword':
      return renderForgotPassword(props);
    case 'resetSent':
      return renderResetSent(props);
    default: // 'login'
      return renderLoginForm(props);
  }
}
